import React, {useEffect, useState} from 'react';
import {Button} from '@mui/material';
import {getStripeSubscription, getStripeInvoices, getUpcomingInvoice, getPortalUri, cancelSubscription, reactivateSubscription} from '../../rest/api';
import type {StripeInvoiceSummary, UpcomingInvoicePreview} from '../../rest/api';
import StripeSubscription from '../../Auth/stripe/subscription';
import {getCurrentFirm} from '../../lib/picos/currentLogin';
import {isDemo} from '../../lib/demo';
import {confirmWarningOkCancel} from '../Confirm';
import Alert from '../../lib/alert';
import './BillingDialog.less';

type BillingDialogProps = {
    onClose?: () => void;
};

/** The firm is cancelled, but its refund is not done: Support settles it, so never say it was made. */
export const REFUND_OWED_MESSAGE = 'Your subscription is cancelled, but your refund has not gone through yet. Support will follow up.';

export const REFUND_UNCONFIRMED_MESSAGE = 'Your subscription is cancelled, but we could not confirm your refund. Please contact Support.';

const fmt = (cents: number, currency: string = 'usd') =>
    new Intl.NumberFormat('en-US', {style: 'currency', currency}).format(cents / 100);

const fmtDate = (epoch: number) => new Date(epoch * 1000).toLocaleDateString();

export default function BillingDialog({onClose}: BillingDialogProps) {
    const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
    const [invoices, setInvoices] = useState<StripeInvoiceSummary[]>([]);
    const [upcoming, setUpcoming] = useState<UpcomingInvoicePreview | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionPending, setActionPending] = useState(false);
    const demo = isDemo();

    useEffect(() => {
        let mounted = true;

        Promise.all([
            getStripeSubscription(),
            getStripeInvoices(),
            getUpcomingInvoice(),
        ]).then(([subResult, invResult, upResult]) => {
            if (!mounted) return;

            if (subResult.isOk() && subResult.unwrap())
                setSubscription(new StripeSubscription(subResult.unwrap()));
            else if (subResult.isErr())
                Alert.addSimpleError(`Could not retrieve subscription details: ${subResult.unwrapErr()?.message}`);

            if (invResult.isOk()) setInvoices(invResult.unwrap());
            if (upResult.isOk()) setUpcoming(upResult.unwrap());

            setLoading(false);
        });

        return () => { mounted = false; };
    }, []);

    const handlePortal = () => {
        const customerId = getCurrentFirm().stripeCustomerId;
        const returnUrl = `${window.location.protocol}//${window.location.host}/`;

        getPortalUri(customerId, returnUrl)
            .then(result => {
                if (result.isOk()) window.open(result.unwrap(), '_blank');
                else Alert.addSimpleError(`Could not open billing portal: ${result.unwrapErr()?.message}`);
            });
    };

    const handleCancel = () => {
        confirmWarningOkCancel(
            'Cancel Service',
            'Cancel your subscription now? Your firm becomes read-only immediately. Outlaw refunds the paid, unused part of the billing period through Stripe. You can reactivate from Billing in the user menu.',
            'Cancel Service',
            'Keep Service',
            async () => {
                setActionPending(true);
                const result = await cancelSubscription();
                setActionPending(false);

                if (result.isOk()) {
                    const cancelled = result.unwrap();
                    setSubscription(new StripeSubscription(cancelled));
                    setUpcoming(null);
                    if ((cancelled as {refundOwed?: boolean}).refundOwed) {
                        Alert.addSimpleError(REFUND_OWED_MESSAGE);
                    }
                } else {
                    // Cancellation can succeed and the step after it fail, so ask Stripe which.
                    const current = await getStripeSubscription();
                    if (current.isOk() && current.unwrap()?.status === 'canceled') {
                        setSubscription(new StripeSubscription(current.unwrap()));
                        setUpcoming(null);
                        Alert.addSimpleError(REFUND_UNCONFIRMED_MESSAGE);
                    } else {
                        Alert.addSimpleError(`Could not cancel subscription: ${result.unwrapErr()?.message}`);
                    }
                }
            },
            undefined,
            'error',
        );
    };

    const handleReactivate = async () => {
        setActionPending(true);
        const result = await reactivateSubscription();
        setActionPending(false);

        if (result.isOk()) {
            setSubscription(new StripeSubscription(result.unwrap()));
            // Re-fetch upcoming invoice since it's relevant again
            const upResult = await getUpcomingInvoice();
            if (upResult.isOk()) setUpcoming(upResult.unwrap());
        } else {
            Alert.addSimpleError(`Could not reactivate subscription: ${result.unwrapErr()?.message}`);
        }
    };

    if (loading) {
        return <div className="billing-dialog">
            <div className="billing-dialog__loading"><i>Loading billing information...</i></div>
        </div>;
    }

    const s = subscription;
    const isCanceling = s?.willCancelAtPeriodEnd;
    const isCanceled = s?.isFullyCancelled;
    const isTrial = !!s?.trialEndDate;

    const statusLabel = isCanceled ? 'Canceled' : isCanceling ? 'Canceling' : isTrial ? 'Trial' : 'Active';
    const statusDotClass = isCanceled ? 'canceled' : isCanceling ? 'canceling' : isTrial ? 'trialing' : 'active';

    return (
        <div className="billing-dialog">
            {/* Section 1: Subscription */}
            <div className="billing-dialog__section-title">Subscription</div>

            {s ? (
                <>
                    <div className="billing-dialog__details">
                        <span className="billing-dialog__label">Status</span>
                        <span className="billing-dialog__value">
                            <span className={`billing-dialog__status-dot billing-dialog__status-dot--${statusDotClass}`} />
                            {statusLabel}
                        </span>

                        <span className="billing-dialog__label">Licensed Users</span>
                        <span className="billing-dialog__value">{s.quantity}</span>

                        <span className="billing-dialog__label">Billing Period</span>
                        <span className="billing-dialog__value">
                            {s.currentPeriodStartDate.toLocaleDateString()} &ndash; {s.currentPeriodEndDate.toLocaleDateString()}
                        </span>

                        <span className="billing-dialog__label">Monthly Cost</span>
                        <span className="billing-dialog__value">${s.monthlyCost}</span>

                        <span className="billing-dialog__label">Customer Since</span>
                        <span className="billing-dialog__value">{s.creationDate.toLocaleDateString()}</span>

                        {isTrial && (
                            <>
                                <span className="billing-dialog__label">Trial Ends</span>
                                <span className="billing-dialog__value">{s.trialEndDate.toLocaleDateString()}</span>
                            </>
                        )}

                        {isCanceling && (
                            <>
                                <span className="billing-dialog__label">Canceled On</span>
                                <span className="billing-dialog__value">{s.cancellationDate?.toLocaleDateString()}</span>

                                <span className="billing-dialog__label">Access Until</span>
                                <span className="billing-dialog__value">{s.subscriptionEndDate?.toLocaleDateString()}</span>
                            </>
                        )}
                    </div>

                    {isCanceled && (
                        <div className="billing-dialog__notice">
                            Your firm is read-only. Choose Reactivate Service to resume making changes.
                        </div>
                    )}
                </>
            ) : (
                <div className="billing-dialog__loading"><i>No subscription information available</i></div>
            )}

            {/* Section 2: Upcoming Invoice */}
            {upcoming && !isCanceling && !isCanceled && (
                <>
                    <div className="billing-dialog__section-title">Upcoming Invoice</div>
                    <div className="billing-dialog__upcoming">
                        <div className="billing-dialog__upcoming-header">
                            <span className="billing-dialog__upcoming-date">
                                {upcoming.next_payment_attempt
                                    ? fmtDate(upcoming.next_payment_attempt)
                                    : fmtDate(upcoming.period_end)}
                            </span>
                            <span className="billing-dialog__upcoming-total">
                                {fmt(upcoming.amount_due, upcoming.currency)}
                            </span>
                        </div>
                        {upcoming.lines.length > 0 && (
                            <div className="billing-dialog__upcoming-lines">
                                {upcoming.lines.map((line, i) => (
                                    <div key={i} className="billing-dialog__upcoming-line">
                                        <span>{line.description || 'Subscription'}</span>
                                        <span>{fmt(line.amount, upcoming.currency)}</span>
                                    </div>
                                ))}
                                {upcoming.tax > 0 && (
                                    <div className="billing-dialog__upcoming-tax">
                                        <span>Tax</span>
                                        <span>{fmt(upcoming.tax, upcoming.currency)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Section 3: Recent Invoices */}
            {invoices.length > 0 && (
                <>
                    <div className="billing-dialog__section-title">Recent Invoices</div>
                    <div className="billing-dialog__invoice-table">
                        <div className="billing-dialog__invoice-header">
                            <span>Date</span>
                            <span>Amount</span>
                            <span>Status</span>
                            <span>PDF</span>
                        </div>
                        {invoices.map(inv => (
                            <div key={inv.id} className="billing-dialog__invoice-row">
                                <span>{fmtDate(inv.created)}</span>
                                <span>{fmt(inv.amount_due, inv.currency)}</span>
                                <span className={`billing-dialog__invoice-status billing-dialog__invoice-status--${inv.status}`}>
                                    {inv.status === 'paid' ? 'Paid' : 'Pending'}
                                </span>
                                <span>
                                    {inv.invoice_pdf && (
                                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                                           className="billing-dialog__invoice-pdf">
                                            View
                                        </a>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Section 4: Actions */}
            <div className="billing-dialog__actions">
                <Button variant="outlined" size="small" disabled={demo || actionPending} onClick={handlePortal}>
                    Manage Payment Methods
                </Button>

                {s && isCanceled && (
                    <Button variant="outlined" size="small" color="success" disabled={demo || actionPending}
                            onClick={handleReactivate}>
                        {actionPending ? 'Reactivating...' : 'Reactivate Service'}
                    </Button>
                )}

                {s && !isCanceled && (
                    <Button variant="outlined" size="small" color="error" disabled={demo || actionPending}
                            onClick={handleCancel}>
                        Cancel Service
                    </Button>
                )}
            </div>
        </div>
    );
}
